import {
  getListRequest,
  getListResponse,
  type GetListRequest,
} from '@core-api/workspace-members/workspace-members/{workspace_id}/list';
import type {
  MemberT,
  WorkspaceMembersResponseT,
  WorkspaceRole,
  WorkspaceMemberStatusEnum,
} from '@mentimeter/http-clients';
import userCache, { useUser } from '@mentimeter/user';
import useSWRInfinite from 'swr/infinite';
import camelCase from 'lodash/camelCase';
import { useCallback, useMemo, type ReactNode } from 'react';
import { InvalidationContextProvider } from './useInvalidateWorkspaceMembersPaginated';
import { useKeyFunction } from './useKeyFunction';

const PAGE_SIZE = 50;

type OrderBy = GetListRequest['orderBy'];
type Order = GetListRequest['order'];

export interface WorkspaceMembersPaginatedOptions {
  filter?: string;
  order?: string;
  orderBy?: OrderBy;
}

export const useWorkspaceMembersPaginated = (
  queryStringOrOptions: string | undefined | WorkspaceMembersPaginatedOptions,
) => {
  const workspaceId = useUser().user?.workspaceId;
  const getKey = useKeyFunction(queryStringOrOptions, PAGE_SIZE);

  const {
    data,
    error,
    size: page,
    setSize: setPage,
    isValidating,
    mutate,
  } = useSWRInfinite<WorkspaceMembersResponseT | undefined>(
    getKey,
    async (urlString) => {
      if (!workspaceId) return;
      const url = new URL(urlString, 'https://dummy.com/');
      const offset = url.searchParams.get('offset');
      /**
       * This looks ugly now but the plan is to improve
       * and clean it up in future iterations.
       * */
      const request = getListRequest({
        region: userCache.region,
        userAuth: userCache.getToken(),
        workspaceId,
        limit: PAGE_SIZE,
        offset: offset ? parseInt(offset) : 0,
        filter: url.searchParams.get('filter') ?? undefined,
        order: (url.searchParams.get('order') ?? 'asc') as Order,
        orderBy: camelCase(
          url.searchParams.get('order_by') ?? 'user_name',
        ) as OrderBy,
        search: url.searchParams.get('search') ?? undefined,
      } as GetListRequest);
      const response = await fetch(request);
      const body = await getListResponse(response);
      return {
        data: body.data.map(
          (m) =>
            ({
              access_level: m.accessLevel,
              email: m.email,
              groups: m.groups,
              id: m.id,
              joined_at: m.joinedAt,
              last_active: m.lastActive,
              name: m.name,
              presentations_count: m.presentationsCount,
              profile_picture_url: m.profilePictureUrl,
              role: m.role as WorkspaceRole,
              status: m.status as WorkspaceMemberStatusEnum,
              updated_at: m.updatedAt,
              user_name: m.userName,
              resource: '',
              url: '',
            }) satisfies MemberT,
        ),
        resource: '',
        url: '',
      };
    },
    {
      shouldRetryOnError: false,
      revalidateFirstPage: false,
    },
  );

  const members = useMemo(
    () => (data?.flatMap((m) => m?.data) || []).filter((m) => m !== undefined),
    [data],
  );

  const isLoadingInitialData = !data && !error;
  const isLoadingMoreMembers =
    isLoadingInitialData ||
    (page > 0 && data && typeof data[page - 1] === 'undefined');
  const isRefreshing = isValidating && data?.length === page;

  const hasReachedLastPage = useMemo(() => {
    if (!data) return false;
    if (data.length === 0) return true;
    const lastPage = data.at(-1);
    if (!lastPage) return true;
    return lastPage.data.length < PAGE_SIZE;
  }, [data]);

  const loadMoreMembers = useCallback(() => {
    if (isLoadingMoreMembers || hasReachedLastPage) return;
    setPage(page + 1);
  }, [hasReachedLastPage, isLoadingMoreMembers, setPage, page]);

  const InvalidationProvider = useCallback(
    ({ children }: { children: ReactNode }) => (
      <InvalidationContextProvider invalidate={mutate}>
        {children}
      </InvalidationContextProvider>
    ),
    [mutate],
  );

  return {
    members,
    page,
    setPage,
    isValidating,
    isLoading: isLoadingInitialData || isLoadingMoreMembers || isRefreshing,
    loadMoreMembers,
    isLoadingInitialMembersData: isLoadingInitialData,
    error,
    mutate,
    InvalidationProvider,
  };
};
